import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import React, { FC, useCallback, useMemo, useState } from 'react';

import {
  type LendingLoanListLoansData,
  type ListLoans,
  type Loan,
  type SearchMetadata,
  lendingLoanGetLoan,
  lendingLoanListLoans,
} from '@column/api';
import { Dropdown, DropdownOption } from '@column/column-ui-kit';

import { useDebounce } from '~/hooks/useDebounce';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import {
  type HighlightSuffixCandidate,
  getHighlightedField,
  getHighlightedSuffix,
  renderHighlight,
} from '~/lib/search/highlight';
import { useSessionStore } from '~/stores/Session';

const loanLabel = (loan: Loan) => loan.description || loan.id;

type LoanDropdownQuery = NonNullable<LendingLoanListLoansData['query']>;

const loanDropdownListQueryKey = (platformId?: string, isSandbox?: boolean, query?: LoanDropdownQuery) =>
  ['loanDropdown', 'list', platformId, isSandbox, query] as const;
const loanQueryKey = (id?: string, platformId?: string, isSandbox?: boolean) =>
  ['loans', 'get', id, platformId, isSandbox] as const;

interface UseLoanOptionsConfig {
  value?: string | null;
}

type LoanWithSearchMetadata = Loan & {
  search_metadata?: SearchMetadata;
};

const SUFFIX_CANDIDATES: HighlightSuffixCandidate[] = [
  { field: 'PRIMARY_SIGNER_ENTITY_NAME', label: 'Primary Signer' },
  { field: 'PRIMARY_SIGNER_ENTITY_ID', label: 'Primary Signer ID' },
  { field: 'DESCRIPTION', label: 'Description' },
  { field: 'LOAN_ID', label: 'ID' },
];

const HighlightedLabel: FC<{ loan: LoanWithSearchMetadata }> = ({ loan }) => {
  const highlightedDescription = getHighlightedField(loan.search_metadata, 'DESCRIPTION');
  const highlightedID = getHighlightedField(loan.search_metadata, 'LOAN_ID');

  if (highlightedDescription) return <>{renderHighlight(highlightedDescription)}</>;
  if (loan.description) return <>{loan.description}</>;
  return <>{highlightedID ? renderHighlight(highlightedID) : loan.id}</>;
};

const PlainLabel: FC<{ loan: Loan }> = ({ loan }) => <>{loanLabel(loan)}</>;

const LoanOptionLabel: FC<{
  loan: LoanWithSearchMetadata;
  highlightSearch: boolean;
  hasSearchQuery: boolean;
}> = ({ loan, highlightSearch, hasSearchQuery }) => {
  if (hasSearchQuery && highlightSearch && loan.search_metadata?.highlights) {
    return <HighlightedLabel loan={loan} />;
  }
  return <PlainLabel loan={loan} />;
};

const LoanOptionSmall: FC<{ loan: LoanWithSearchMetadata }> = ({ loan }) => {
  const highlightedDescription = getHighlightedField(loan.search_metadata, 'DESCRIPTION');
  const highlightedID = getHighlightedField(loan.search_metadata, 'LOAN_ID');
  const candidates = SUFFIX_CANDIDATES.filter((candidate) => {
    if (candidate.field === 'DESCRIPTION' && highlightedDescription) return false;
    if (candidate.field === 'LOAN_ID' && !highlightedDescription && !loan.description && highlightedID) return false;
    return true;
  });
  const match = getHighlightedSuffix(loan.search_metadata, candidates);
  if (!match) return null;
  return (
    <>
      {match.label}: {renderHighlight(match.fragment)}
    </>
  );
};

export const useLoanOptions = (config: UseLoanOptionsConfig = {}) => {
  const { value } = config;
  const platformId = useSessionStore((state) => state.currentPlatform?.id);
  const isSandbox = useSessionStore((state) => state.isSandbox);
  const resolvedId = value ?? undefined;
  const isLoanSearchEnabled = useFeatureFlag(FeatureFlag.EnableLoanSearch);
  const [searchText, setSearchText] = useState<string | undefined>();
  const effectiveSearchText = isLoanSearchEnabled ? searchText : undefined;
  const effectiveQuery = useMemo<LoanDropdownQuery | undefined>(() => {
    if (!effectiveSearchText) return undefined;
    return { query: effectiveSearchText };
  }, [effectiveSearchText]);

  const loansQuery = useInfiniteQuery({
    queryKey: loanDropdownListQueryKey(platformId, isSandbox, effectiveQuery),
    queryFn: async ({ pageParam, signal }: { pageParam?: string; signal?: AbortSignal }) => {
      const { data } = await lendingLoanListLoans({
        query: { ...effectiveQuery, starting_after: pageParam },
        signal,
        throwOnError: true,
      });
      return data as ListLoans;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.has_more) return undefined;
      return lastPage.loans[lastPage.loans.length - 1]?.id;
    },
    initialPageParam: undefined as string | undefined,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  const loans = useMemo(() => {
    const entries = loansQuery.data?.pages.flatMap((page) => page.loans) ?? [];
    return [...new Map(entries.map((entry) => [entry.id, entry])).values()];
  }, [loansQuery.data]);

  const activeLoanQuery = useQuery<LoanWithSearchMetadata>({
    queryKey: loanQueryKey(resolvedId, platformId, isSandbox),
    queryFn: async ({ signal }) => {
      const { data } = await lendingLoanGetLoan({
        path: { loan_id: resolvedId! },
        signal,
        throwOnError: true,
      });
      return data as LoanWithSearchMetadata;
    },
    enabled: Boolean(resolvedId),
    placeholderData: () => loans.find((loan) => loan.id === resolvedId),
    staleTime: 5 * 60_000,
  });

  const activeLoan = activeLoanQuery.data;
  const hasSearchQuery = Boolean(searchText);
  const [search] = useDebounce((query?: string) => setSearchText(query || undefined), 300, []);

  const toOption = useCallback(
    (loan: LoanWithSearchMetadata, highlightSearch: boolean): DropdownOption => ({
      label: <LoanOptionLabel loan={loan} highlightSearch={highlightSearch} hasSearchQuery={hasSearchQuery} />,
      small:
        hasSearchQuery && highlightSearch && loan.search_metadata?.highlights ? (
          <LoanOptionSmall loan={loan} />
        ) : undefined,
      value: loan.id,
    }),
    [hasSearchQuery]
  );

  const options = useMemo(() => loans.map((loan) => toOption(loan, true)), [loans, toOption]);

  const hiddenOptions = useMemo(() => {
    if (!resolvedId) return [];

    const selected =
      loans.find((loan) => loan.id === resolvedId) || (activeLoan?.id === resolvedId ? activeLoan : undefined);

    return selected ? [toOption(selected, false)] : [];
  }, [activeLoan, resolvedId, loans, toOption]);

  const { fetchNextPage, hasNextPage, isFetchingNextPage, isPlaceholderData } = loansQuery;
  const handleScrollEnd = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && !isPlaceholderData) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isPlaceholderData]);

  return {
    options,
    hiddenOptions,
    handleScrollEnd,
    search,
    loans,
  };
};

export interface LoanDropdownProps {
  value?: string | null;
  onChange: (value: string) => void;
  fullWidth?: boolean;
  maxWidth?: string;
  hasError?: boolean;
  placeholder?: string;
  isDisabled?: boolean;
}

export const LoanDropdown: FC<LoanDropdownProps> = ({
  value,
  onChange,
  fullWidth,
  maxWidth,
  hasError,
  placeholder,
  isDisabled,
}) => {
  const isLoanSearchEnabled = useFeatureFlag(FeatureFlag.EnableLoanSearch);
  const { options, hiddenOptions, handleScrollEnd, search } = useLoanOptions({ value });

  return (
    <Dropdown
      options={options}
      hiddenOptions={hiddenOptions}
      active={value ?? undefined}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      emptyMessage="No loans found"
      onSearchChange={isLoanSearchEnabled ? (query: string) => search(query) : undefined}
      onChange={onChange}
      onScrollEnd={handleScrollEnd}
      hasError={hasError}
      isDisabled={isDisabled}
      placeholder={placeholder}
      search={isLoanSearchEnabled}
    />
  );
};
